/**
 * One line for the AI on your computer. What you ask (chat, a receipt scan, "Suggest moves",
 * looking up products) goes first, one at a time in the order you asked, so the model isn't
 * doing two things at once. Background work (catalog lookups, rewards rates, money suggestions)
 * runs only when nothing you asked for is waiting and you're idle or it's overnight, and stops
 * the moment you ask something; a stopped job picks up again later.
 *
 * Everything shows under "All changes saved": what's running, with a bar that fills as it goes,
 * and what's queued next.
 */
const KEY = "ai:background";

export type BackgroundMode = "off" | "idle" | "overnight";

export interface BackgroundJob {
  id: string;
  label: string;
  /** True when there's work to do. */
  due: () => boolean;
  run: (signal: AbortSignal) => Promise<void>;
  /** How far along it is, for the progress bar ([done, total]). */
  progress?: () => [number, number] | null;
}

export interface Activity {
  id: string;
  label: string;
  state: "running" | "queued";
  /** Counted progress ("5 of 44"). */
  done?: number;
  total?: number;
  /** Estimated share done (0–1) when there's nothing to count, e.g. a reply streaming in. */
  share?: number;
  /** Progress read from elsewhere (a lookup's own counter). */
  progress?: () => [number, number] | null;
}

function readMode(): BackgroundMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === "off" || v === "overnight" ? v : "idle";
  } catch {
    return "idle";
  }
}

const IDLE_MS = 2 * 60_000;

class AiQueue {
  mode = $state<BackgroundMode>(readMode());
  /** What the background is doing right now, if anything. */
  current = $state<string | null>(null);
  /** The background job that's running (for its progress). */
  currentJob = $state.raw<BackgroundJob | null>(null);
  /** What you asked for that the AI is working on right now ("Ask", "Receipt"). */
  asked = $state<string | null>(null);
  /** Everything you asked for: the one running, then the ones waiting their turn. */
  activities = $state<Activity[]>([]);
  /** Bumped every few seconds so "what's due next" stays current. */
  private beat = $state(0);
  private nextId = 0;
  private chain: Promise<unknown> = Promise.resolve();
  private foreground = 0;
  private jobs: BackgroundJob[] = [];
  private ctrl: AbortController | null = null;
  private lastInput = Date.now();
  private started = false;

  /** Update one of your jobs' label or counted progress ("Costco receipt 10/26", 5 of 44). */
  report(id: string, label: string, done?: number, total?: number) {
    const a = this.activities.find((x) => x.id === id);
    if (a) Object.assign(a, { label, done, total, share: total ? undefined : a.share });
  }

  /**
   * A reply streaming in with nothing to count: estimate from how much has arrived against
   * about how long such a reply usually is. It never shows full until it's done.
   */
  streamed(id: string, chars: number, expected: number) {
    const a = this.activities.find((x) => x.id === id);
    if (a && !a.total) a.share = Math.min(0.95, chars / Math.max(1, expected));
  }

  setMode(m: BackgroundMode) {
    this.mode = m;
    try {
      localStorage.setItem(KEY, m);
    } catch {
      /* fine */
    }
    if (m === "off") this.ctrl?.abort();
  }

  register(job: BackgroundJob) {
    this.jobs = [...this.jobs.filter((j) => j.id !== job.id), job];
  }

  /** Something you asked for: it waits for anything else you asked first, then runs. */
  async first<T>(
    fn: (id: string) => Promise<T>,
    label = "Working",
    progress?: () => [number, number] | null,
  ): Promise<T> {
    this.foreground++;
    this.ctrl?.abort();
    const id = `job-${++this.nextId}`;
    this.activities.push({ id, label, state: "queued", progress });
    const run = this.chain.then(() => {
      const a = this.activities.find((x) => x.id === id);
      if (a) a.state = "running";
      this.asked = a?.label ?? label;
      return fn(id);
    });
    this.chain = run.catch(() => {});
    try {
      return await run;
    } finally {
      this.foreground--;
      this.activities = this.activities.filter((x) => x.id !== id);
      this.asked = this.activities.find((x) => x.state === "running")?.label ?? null;
    }
  }

  /** Background jobs with work waiting (shown as "next"), not counting the one running. */
  get waiting(): BackgroundJob[] {
    void this.beat;
    return this.jobs.filter((j) => {
      if (j.id === this.currentJob?.id || this.mode === "off") return false;
      try {
        return j.due();
      } catch {
        return false;
      }
    });
  }

  /** Whether background work may run right now. */
  allowed(now = new Date()): boolean {
    if (this.mode === "off" || this.foreground > 0) return false;
    if (this.mode === "overnight") {
      const h = now.getHours();
      return h >= 23 || h < 7;
    }
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    return hidden || Date.now() - this.lastInput > IDLE_MS;
  }

  private async tick() {
    if (this.current || !this.allowed()) return;
    const job = this.jobs.find((j) => {
      try {
        return j.due();
      } catch {
        return false;
      }
    });
    if (!job) return;
    this.ctrl = new AbortController();
    this.current = job.label;
    this.currentJob = job;
    try {
      await job.run(this.ctrl.signal);
    } catch {
      /* it tries again on a later tick */
    } finally {
      this.current = null;
      this.currentJob = null;
      this.ctrl = null;
    }
  }

  start() {
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    const touch = () => {
      this.lastInput = Date.now();
    };
    for (const ev of ["pointerdown", "keydown", "scroll"])
      window.addEventListener(ev, touch, { passive: true });
    setInterval(() => void this.tick(), 30_000);
    setInterval(() => this.beat++, 5_000);
  }
}

export const aiQueue = new AiQueue();
